import React from 'react';
import styled from 'styled-components';
import CodeIcon from '@mui/icons-material/Code';

const Button = styled.button`
    display: none;
    width: 100%;
    padding: 10px;
    background-color: ${({ theme }) => theme.white};
    color: ${({ theme }) => theme.text_black};
    font-size: 14px;
    font-weight: 700;
    border: none;
    border-radius: 10px;
    cursor: pointer;
    transition: all 0.8s ease-in-out;
`;

const Card = styled.div`
    width: 330px;
    height: 490px;
    background-color: ${({ theme }) => theme.card};
    cursor: pointer;
    border-radius: 12px;
    box-shadow: 0 0 12px 4px rgba(0,0,0,0.4);
    overflow: hidden;
    padding: 26px 20px;
    display: flex;
    flex-direction: column;
    gap: 14px;
    transition: all 0.35s ease-in-out;
    position: relative;
    &:hover {
        transform: translateY(-8px) scale(1.015);
        box-shadow: 0 12px 40px 4px rgba(133, 76, 230, 0.28);
        filter: brightness(1.05);
    }
    &:hover ${Button} {
        display: block;
    }
    @media (max-width: 900px) {
        width: 280px;
        height: 460px;
        padding: 18px 12px;
    }
    @media (max-width: 600px) {
        width: 95vw;
        height: auto;
        min-width: 0;
        padding: 14px 10px;
    }
`;

const Image = styled.img`
    width: 100%;
    height: 180px;
    object-fit: cover;
    background-color: ${({ theme }) => theme.white};
    border-radius: 10px;
    box-shadow: 0 0 16px 2px rgba(0,0,0,0.3);
`;

const ImagePlaceholder = styled.div`
    width: 100%;
    height: 180px;
    background: linear-gradient(135deg, #1f2634 0%, #2b1055 100%);
    border-radius: 10px;
    box-shadow: 0 0 16px 2px rgba(0,0,0,0.3);
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 8px;
    color: #b1b2b3;
    border: 1px dashed rgba(255, 255, 255, 0.2);
`;

const Tags = styled.div`
    width: 100%;
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 8px;
    margin-top: 4px;
`;

const Tag = styled.span`
    font-size: 12px;
    font-weight: 400;
    color: ${({ theme }) => theme.primary};
    background-color: ${({ theme }) => theme.primary + '18'};
    padding: 2px 8px;
    border-radius: 8px;
`;

const Details = styled.div`
    width: 100%;
    display: flex;
    flex-direction: column;
    gap: 4px;
    padding: 0px 2px;
`;

const Title = styled.div`
    font-size: 18px;
    font-weight: 600;
    color: ${({ theme }) => theme.text_secondary};
    overflow: hidden;
    display: -webkit-box;
    max-width: 100%;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    text-overflow: ellipsis;
`;

const Date = styled.div`
    font-size: 12px;
    margin-left: 2px;
    font-weight: 400;
    color: ${({ theme }) => theme.text_secondary + '80'};
    @media only screen and (max-width: 768px){
        font-size: 10px;
    }
`;

const Description = styled.div`
    font-weight: 400;
    color: ${({ theme }) => theme.text_secondary + 'cc'};
    overflow: hidden;
    margin-top: 6px;
    display: -webkit-box;
    max-width: 100%;
    -webkit-line-clamp: 3;
    -webkit-box-orient: vertical;
    text-overflow: ellipsis;
    font-size: 14px;
    line-height: 1.5;
`;

const Members = styled.div`
    display: flex;
    align-items: center;
    padding-left: 10px;
`;

const Avatar = styled.img`
    width: 38px;
    height: 38px;
    border-radius: 50%;
    margin-left: -10px;
    background-color: ${({ theme }) => theme.white};
    box-shadow: 0 0 10px rgba(0,0,0,0.2);
    border: 3px solid ${({ theme }) => theme.card};
`;

const FeaturedBadge = styled.span`
    position: absolute;
    top: 12px;
    right: 12px;
    font-size: 10px;
    font-weight: 700;
    letter-spacing: 0.5px;
    text-transform: uppercase;
    color: #ffffff;
    background: linear-gradient(135deg, #854CE6 0%, #a855f7 100%);
    padding: 3px 9px;
    border-radius: 20px;
    box-shadow: 0 2px 8px rgba(133, 76, 230, 0.45);
    z-index: 2;
`;

const ProjectCards = ({ project, setOpenModal }) => {
    const isFeatured = project.featured || project.category === 'Industry-Sponsored';
    return (
        <Card onClick={() => setOpenModal({ state: true, project: project })}>
            {isFeatured && <FeaturedBadge>⭐ Featured</FeaturedBadge>}
            {project.img ? (
                <Image src={project.img} alt={project.title} />
            ) : (
                <ImagePlaceholder>
                    <CodeIcon style={{ fontSize: 40, color: '#854CE6' }} />
                    <span style={{ fontSize: 13, fontWeight: 500 }}>{project.title.split('(')[0]}</span>
                    <span style={{ fontSize: 11, color: '#854CE6', opacity: 0.8 }}>TODO: Add Screenshot</span>
                </ImagePlaceholder>
            )}
            <Tags>
                {project.tags?.map((tag, index) => (
                    <Tag key={tag + index}>{tag}</Tag>
                ))}
            </Tags>
            <Details>
                <Title>{project.title}</Title>
                <Date>{project.date}</Date>
                <Description>{project.description}</Description>
            </Details>
            {project.member && (
                <Members>
                    {project.member.map((member, idx) => (
                        <Avatar key={member.github || member.linkedin || idx} src={member.img} alt={member.name} />
                    ))}
                </Members>
            )}
        </Card>
    );
};

export default ProjectCards;
